// System > Billing — operator subscription/billing management (#1050, completes the
// operator half of #1002).
//
// Surfaces the existing Square billing backend (api/routes/billing.js, #519):
//   • Square credential status + update (token is write-only; never echoed back)
//   • Per-tenant seat counts (desk $15 / mobile $7) + computed monthly amount
//   • Invoice creation (publish, or dry-run DRAFT that sends no email)
//   • Per-tenant invoice history (stratus_ext.square_invoices)
//
// Access: reached via the superOnly System nav group (super_admin / reseller /
// System app_role). The API guard additionally rejects anyone who is not
// super_admin or reseller.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ExternalLink, FileText } from 'lucide-react';
import { api, BillingTenant, BillingInvoice } from '../lib/api';
import { Dialog } from '../components/Dialog';
import { useToast } from '../components/Toast';

const fmtUsd = (n: number) => `$${n.toFixed(2)}`;
const fmtCents = (c: number) => fmtUsd((c || 0) / 100);
const fmtDate = (s?: string | null) => (s ? String(s).slice(0, 10) : '—');
const currentPeriod = () => new Date().toISOString().slice(0, 7);

function StatusBadge({ status, dry_run }: { status: string; dry_run?: boolean }) {
  const cls = status === 'published' ? 'badge-up' : status === 'draft' ? 'badge-warn' : 'badge-down';
  return <span className={cls}>{status}{dry_run ? ' (dry run)' : ''}</span>;
}

export default function Billing() {
  const toast = useToast();
  const qc = useQueryClient();

  const { data: summary, isLoading, error } = useQuery({
    queryKey: ['billing-summary'],
    queryFn: () => api.billing.summary(),
  });
  const { data: settings } = useQuery({
    queryKey: ['billing-settings'],
    queryFn: () => api.billing.settings(),
  });

  const [expanded, setExpanded] = useState<string | null>(null);
  const [invoiceFor, setInvoiceFor] = useState<BillingTenant | null>(null);

  const tenants = summary?.tenants ?? [];
  const totals = summary?.totals;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Billing</h1>
        <div className="text-sm text-textSecondary">
          Desk seat $15.00 / mo · Mobile seat $7.00 / mo
        </div>
      </div>

      <SquareSettingsCard settings={settings} onSaved={() => qc.invalidateQueries({ queryKey: ['billing-settings'] })} />

      {/* Totals header */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Tenants" value={tenants.length} />
        <Stat label="Desk Seats" value={totals?.desk_seats ?? '—'} />
        <Stat label="Mobile Seats" value={totals?.mobile_seats ?? '—'} />
        <Stat label="Monthly Total" value={totals ? fmtUsd(totals.monthly_total) : '—'} />
      </div>

      {/* Per-tenant table */}
      <div className="card p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-4 text-sm text-textSecondary">Loading billing summary…</div>
        ) : error ? (
          <div className="p-4 text-sm text-danger">{(error as Error).message}</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th className="w-6"></th>
                <th>Tenant</th><th>Status</th>
                <th className="text-right">Desk</th><th className="text-right">Mobile</th>
                <th className="text-right">Monthly</th>
                <th>Square Customer</th><th>Last Invoice</th><th></th>
              </tr>
            </thead>
            <tbody>
              {tenants.map(t => (
                <TenantRows
                  key={t.domain_uuid}
                  tenant={t}
                  expanded={expanded === t.domain_uuid}
                  onToggle={() => setExpanded(prev => (prev === t.domain_uuid ? null : t.domain_uuid))}
                  onInvoice={() => setInvoiceFor(t)}
                />
              ))}
              {!tenants.length && (
                <tr><td colSpan={9} className="text-center text-textSecondary py-6">No tenants found.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <CreateInvoiceDialog
        tenant={invoiceFor}
        onClose={() => setInvoiceFor(null)}
        onCreated={(msg) => {
          toast('success', msg);
          setInvoiceFor(null);
          qc.invalidateQueries({ queryKey: ['billing-summary'] });
          qc.invalidateQueries({ queryKey: ['billing-invoices'] });
        }}
        onError={(msg) => toast('error', msg)}
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: any }) {
  return (
    <div className="card text-center">
      <div className="text-textSecondary text-sm">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

// ── Square credentials ────────────────────────────────────────────────────────

function SquareSettingsCard({ settings, onSaved }: {
  settings?: { token_configured: boolean; location_id: string | null; token_from_env: boolean;
               location_from_env: boolean; updated_at?: string; updated_by?: string };
  onSaved: () => void;
}) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [token, setToken] = useState('');
  const [locationId, setLocationId] = useState('');

  const saveM = useMutation({
    mutationFn: () => api.billing.saveSettings({
      ...(token ? { access_token: token } : {}),
      ...(locationId ? { location_id: locationId } : {}),
    }),
    onSuccess: () => {
      toast('success', 'Square settings saved');
      setEditing(false); setToken(''); setLocationId('');
      onSaved();
    },
    onError: (e: any) => toast('error', e?.message || 'Failed to save Square settings'),
  });

  const tokenOk = !!settings && (settings.token_from_env || settings.token_configured);
  const locOk = !!settings && (settings.location_from_env || !!settings.location_id);

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-medium text-sm">Square Payment Account</div>
        {!editing && <button className="btn-ghost text-sm" onClick={() => setEditing(true)}>Update credentials…</button>}
      </div>
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-textSecondary">Access token:</span>
          {tokenOk ? <span className="badge-up">configured{settings?.token_from_env ? ' (env)' : ''}</span>
                   : <span className="badge-down">missing</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-textSecondary">Location:</span>
          {locOk ? <span className="badge-up">{settings?.location_from_env ? 'from env' : settings?.location_id}</span>
                 : <span className="badge-down">missing</span>}
        </div>
        {settings?.updated_at && (
          <div className="text-textSecondary">
            Last updated {fmtDate(settings.updated_at)}{settings.updated_by ? ` by ${settings.updated_by}` : ''}
          </div>
        )}
      </div>
      {!tokenOk && !editing && (
        <div className="text-xs text-warning">
          Invoices cannot be created until a Square access token is configured (env var or below).
        </div>
      )}
      {editing && (
        <div className="space-y-2 pt-2 border-t border-border">
          <div className="grid md:grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-textSecondary">Access token (write-only; stored encrypted)</label>
              <input className="input" type="password" autoComplete="off" value={token}
                     onChange={e => setToken(e.target.value)} placeholder="Leave blank to keep current" />
            </div>
            <div>
              <label className="text-xs text-textSecondary">Location ID</label>
              <input className="input" value={locationId}
                     onChange={e => setLocationId(e.target.value)} placeholder={settings?.location_id || 'L…'} />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button className="btn-ghost text-sm" onClick={() => { setEditing(false); setToken(''); setLocationId(''); }}>Cancel</button>
            <button className="btn-primary text-sm" disabled={saveM.isPending || (!token && !locationId)}
                    onClick={() => saveM.mutate()}>
              {saveM.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tenant row + expandable invoice history ───────────────────────────────────

function TenantRows({ tenant: t, expanded, onToggle, onInvoice }: {
  tenant: BillingTenant; expanded: boolean; onToggle: () => void; onInvoice: () => void;
}) {
  return (
    <>
      <tr className="hover:bg-elevated cursor-pointer" onClick={onToggle}>
        <td className="text-textSecondary">{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
        <td>{t.domain_description || t.domain_name}</td>
        <td>{t.domain_enabled === 'true' ? <span className="badge-up">enabled</span> : <span className="badge-down">disabled</span>}</td>
        <td className="text-right font-mono">{t.desk_seats}</td>
        <td className="text-right font-mono">{t.mobile_seats}</td>
        <td className="text-right font-mono font-semibold">{fmtUsd(t.monthly_total)}</td>
        <td>{t.square_customer_linked ? <span className="badge-up">linked</span> : <span className="text-textSecondary text-xs">not linked</span>}</td>
        <td>
          {t.last_invoice ? (
            <span className="inline-flex items-center gap-2">
              <StatusBadge status={t.last_invoice.status} dry_run={t.last_invoice.dry_run} />
              <span className="text-textSecondary text-xs">{fmtDate(t.last_invoice.period_start)} · {fmtCents(t.last_invoice.amount_cents)}</span>
            </span>
          ) : <span className="text-textSecondary text-xs">never invoiced</span>}
        </td>
        <td onClick={e => e.stopPropagation()}>
          <button className="btn-ghost text-sm inline-flex items-center gap-1" onClick={onInvoice}>
            <FileText size={14} /> Invoice…
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={9} className="bg-elevated/50 p-0">
            <InvoiceHistory domainUuid={t.domain_uuid} />
          </td>
        </tr>
      )}
    </>
  );
}

function InvoiceHistory({ domainUuid }: { domainUuid: string }) {
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['billing-invoices', domainUuid],
    queryFn: () => api.billing.invoices(domainUuid),
  });

  if (isLoading) return <div className="p-3 text-sm text-textSecondary">Loading invoices…</div>;
  if (!invoices.length) return <div className="p-3 text-sm text-textSecondary">No invoices recorded for this tenant.</div>;

  return (
    <table className="table">
      <thead>
        <tr>
          <th>Period</th><th>Desk</th><th>Mobile</th><th>Amount</th>
          <th>Status</th><th>Created</th><th>By</th><th>Square</th>
        </tr>
      </thead>
      <tbody>
        {(invoices as BillingInvoice[]).map(inv => (
          <tr key={inv.invoice_uuid}>
            <td className="font-mono text-xs">{fmtDate(inv.period_start)} → {fmtDate(inv.period_end)}</td>
            <td className="font-mono">{inv.desk_seats}</td>
            <td className="font-mono">{inv.mobile_seats}</td>
            <td className="font-mono">{fmtCents(inv.amount_cents)}</td>
            <td><StatusBadge status={inv.status} dry_run={inv.dry_run} /></td>
            <td className="text-xs">{fmtDate(inv.created_at)}</td>
            <td className="text-xs">{inv.created_by}</td>
            <td>
              {inv.invoice_url ? (
                <a href={inv.invoice_url} target="_blank" rel="noreferrer"
                   className="text-primary inline-flex items-center gap-1 text-xs">
                  view <ExternalLink size={12} />
                </a>
              ) : <span className="text-textSecondary text-xs">—</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Create invoice dialog ─────────────────────────────────────────────────────

function CreateInvoiceDialog({ tenant, onClose, onCreated, onError }: {
  tenant: BillingTenant | null;
  onClose: () => void;
  onCreated: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [period, setPeriod] = useState(currentPeriod());
  const [email, setEmail] = useState('');
  const [dryRun, setDryRun] = useState(true);

  const createM = useMutation({
    mutationFn: () => api.billing.createInvoice({
      domain_uuid: tenant!.domain_uuid,
      period,
      ...(email ? { billing_email: email } : {}),
      dry_run: dryRun,
    }),
    onSuccess: (r: any) => {
      if (r?.skipped) onCreated(`Skipped — ${r.reason || 'no billable seats'}.`);
      else onCreated(dryRun
        ? `Draft invoice created for ${fmtUsd(r?.monthly_total ?? 0)} (no email sent).`
        : `Invoice published for ${fmtUsd(r?.monthly_total ?? 0)} — Square will email the tenant.`);
    },
    onError: (e: any) => onError(e?.message || 'Invoice creation failed'),
  });

  if (!tenant) return null;
  const name = tenant.domain_description || tenant.domain_name;

  return (
    <Dialog open onClose={onClose} title={`Create Invoice — ${name}`} size="sm"
      footer={
        <>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={createM.isPending || !/^\d{4}-\d{2}$/.test(period)}
                  onClick={() => createM.mutate()}>
            {createM.isPending ? 'Creating…' : dryRun ? 'Create Draft' : 'Create & Publish'}
          </button>
        </>
      }>
      <div className="space-y-3 text-sm">
        <div className="card bg-elevated flex justify-between">
          <span>{tenant.desk_seats} desk + {tenant.mobile_seats} mobile seats</span>
          <span className="font-mono font-semibold">{fmtUsd(tenant.monthly_total)}/mo</span>
        </div>
        <div>
          <label className="text-xs text-textSecondary">Billing period (YYYY-MM)</label>
          <input className="input" value={period} onChange={e => setPeriod(e.target.value)} placeholder="2026-07" />
        </div>
        <div>
          <label className="text-xs text-textSecondary">Billing email override (optional)</label>
          <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)}
                 placeholder="Uses the tenant's saved billing email when blank" />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} />
          <span>Dry run — create a DRAFT in Square, send no email</span>
        </label>
        {!dryRun && (
          <div className="text-xs text-warning">
            Publishing emails a live payable Square invoice to the tenant. Retrying the same
            period is idempotent on Square's side.
          </div>
        )}
      </div>
    </Dialog>
  );
}
